import {
  hasNonDuplicateErrors,
  isClashing,
  type DiffChange,
  type DiffResult
} from '~/components/csv-import/model/diff';

export type RowFilter =
  | 'all'
  | 'errors'
  | 'conflicts'
  | 'duplicates'
  | 'added'
  | 'updated'
  | 'removed';

// Short filter names; the table's sections name the groups themselves (Additions, Removals).
export const ROW_FILTERS: { key: RowFilter; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'errors', label: 'Errors' },
  { key: 'conflicts', label: 'Conflicts' },
  { key: 'duplicates', label: 'Duplicates' },
  { key: 'added', label: 'New' },
  { key: 'updated', label: 'Updates' },
  { key: 'removed', label: 'Delete' }
];

export const matchesFilter = (
  filter: RowFilter,
  change: DiffChange,
  result: DiffResult
): boolean => {
  switch (filter) {
    case 'all':
      return true;
    case 'errors':
      return hasNonDuplicateErrors(change);
    case 'conflicts':
      // Still listed once one is chosen, so the choice can be changed.
      return isClashing(change, result);
    case 'duplicates':
      // Rows already in the list, and extra copies within the file.
      return change.repeatOf !== undefined || change.classification === 'unchanged';
    case 'added':
    case 'updated':
    case 'removed':
      // Only one copy is imported, so extra copies count under Duplicates alone.
      return change.classification === filter && change.repeatOf === undefined;
    default: {
      const _exhaustive: never = filter;
      return _exhaustive;
    }
  }
};

export const countByFilter = (result: DiffResult): Record<RowFilter, number> =>
  Object.fromEntries(
    ROW_FILTERS.map(({ key }) => [
      key,
      result.changes.filter((change) => matchesFilter(key, change, result)).length
    ])
  ) as Record<RowFilter, number>;
